import React from 'react';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { AutoComplete, AutoCompleteChangeEvent } from 'primereact/autocomplete';
import { Calendar } from 'primereact/calendar';
import { Divider } from 'primereact/divider';
import { classNames } from 'primereact/utils';
import { Product } from '../../../types/product';
import { parseDateOnly, formatDateOnly } from '../../../utils';
import { EditablePurchaseOrderItem, EditableHandlingUnit, ItemFieldErrors } from './types';
import HandlingUnitsTable from './HandlingUnitsTable';

interface ItemEditorProps {
  item: EditablePurchaseOrderItem;
  index: number;
  filteredProducts: Product[];
  errors?: ItemFieldErrors;
  newHU: { huNumber?: string };
  onUpdateItem: (index: number, field: keyof EditablePurchaseOrderItem | 'material', value: any) => void;
  onSearchProducts: (event: { query: string }) => Promise<void>;
  onUpdateHandlingUnit: (itemIndex: number, huIndex: number, field: keyof EditableHandlingUnit, value: any) => void;
  onRemoveHandlingUnit: (itemIndex: number, huIndex: number) => void;
  onAddHandlingUnit: (itemIndex: number) => void;
  onNewHUChange: (huNumber: string) => void;
}

/**
 * Well-spaced editable form for a single purchase-order line.
 * Rendered inside the expanded row of the items list.
 */
const ItemEditor: React.FC<ItemEditorProps> = ({
  item,
  index,
  filteredProducts,
  errors,
  newHU,
  onUpdateItem,
  onSearchProducts,
  onUpdateHandlingUnit,
  onRemoveHandlingUnit,
  onAddHandlingUnit,
  onNewHUChange
}) => {
  // AutoComplete accepts either a typed string (free-text material) or a
  // selected product object. field="name" makes the object render its name.
  const materialValue: Product | string = item.product ?? (item.material || '');

  const handleMaterialChange = (e: AutoCompleteChangeEvent) => {
    const value = e.value;
    if (value && typeof value === 'object') {
      // Product selected from suggestions
      onUpdateItem(index, 'product', value);
    } else {
      // Free text typed — kept as material string, productId resolved on save
      onUpdateItem(index, 'material', typeof value === 'string' ? value : '');
    }
  };

  const quantityNumber =
    item.quantity === '' || item.quantity == null ? null : Number(item.quantity);
  const unitPriceNumber =
    item.unitPrice === '' || item.unitPrice == null ? null : Number(item.unitPrice);

  return (
    <div className="p-2">
      <div className="formgrid grid">
        <div className="field col-12 md:col-2">
          <label htmlFor={`itemNumber-${index}`} className="text-sm">Item Number *</label>
          <InputText
            id={`itemNumber-${index}`}
            value={item.itemNumber || ''}
            onChange={(e) => onUpdateItem(index, 'itemNumber', e.target.value)}
            placeholder="e.g. 10"
            className={classNames('w-full', { 'p-invalid': errors?.itemNumber })}
          />
          {errors?.itemNumber && <small className="p-error">{errors.itemNumber}</small>}
        </div>

        <div className="field col-12 md:col-6">
          <label htmlFor={`material-${index}`} className="text-sm">Material *</label>
          <AutoComplete
            inputId={`material-${index}`}
            value={materialValue}
            suggestions={filteredProducts}
            completeMethod={onSearchProducts}
            field="name"
            dropdown
            forceSelection={false}
            onChange={handleMaterialChange}
            placeholder="Search product or type material number"
            className={classNames('w-full', { 'p-invalid': errors?.material })}
            inputClassName="w-full"
            delay={300}
          />
          {item.product ? (
            <small className="text-color-secondary">
              Selected: {item.product.name} (ID: {item.product.id})
            </small>
          ) : item.material ? (
            <small className="text-color-secondary">
              New material "{item.material}" — product will be created on save.
            </small>
          ) : null}
          {errors?.material && <small className="p-error block">{errors.material}</small>}
        </div>

        <div className="field col-12 md:col-2">
          <label htmlFor={`quantity-${index}`} className="text-sm">Quantity *</label>
          <InputNumber
            inputId={`quantity-${index}`}
            value={quantityNumber}
            onValueChange={(e) => onUpdateItem(index, 'quantity', e.value ?? '')}
            placeholder="Qty"
            min={0}
            minFractionDigits={0}
            maxFractionDigits={3}
            className={classNames('w-full', { 'p-invalid': errors?.quantity })}
            inputClassName="w-full"
          />
          {errors?.quantity && <small className="p-error">{errors.quantity}</small>}
        </div>

        <div className="field col-12 md:col-2">
          <label htmlFor={`unitPrice-${index}`} className="text-sm">Unit Price *</label>
          <InputNumber
            inputId={`unitPrice-${index}`}
            value={unitPriceNumber}
            onValueChange={(e) => onUpdateItem(index, 'unitPrice', e.value ?? '')}
            placeholder="Price"
            min={0}
            mode="decimal"
            minFractionDigits={2}
            maxFractionDigits={2}
            className={classNames('w-full', { 'p-invalid': errors?.unitPrice })}
            inputClassName="w-full"
          />
          {errors?.unitPrice && <small className="p-error">{errors.unitPrice}</small>}
        </div>

        <div className="field col-12 md:col-4">
          <label htmlFor={`shipToParty-${index}`} className="text-sm">Ship To *</label>
          <InputText
            id={`shipToParty-${index}`}
            value={item.shipToParty || ''}
            onChange={(e) => onUpdateItem(index, 'shipToParty', e.target.value)}
            placeholder="Ship to party"
            className={classNames('w-full', { 'p-invalid': errors?.shipToParty })}
          />
          {errors?.shipToParty && <small className="p-error">{errors.shipToParty}</small>}
        </div>

        <div className="field col-12 md:col-4">
          <label htmlFor={`batchNumber-${index}`} className="text-sm">Batch</label>
          <InputText
            id={`batchNumber-${index}`}
            value={item.batchNumber || ''}
            onChange={(e) => onUpdateItem(index, 'batchNumber', e.target.value)}
            placeholder="Batch number"
            className="w-full"
          />
        </div>

        <div className="field col-12 md:col-4">
          <label htmlFor={`deliveryDate-${index}`} className="text-sm">Delivery Date *</label>
          <Calendar
            inputId={`deliveryDate-${index}`}
            value={parseDateOnly(item.deliveryDate)}
            onChange={(e) => {
              const date = e.value as Date | null;
              onUpdateItem(index, 'deliveryDate', formatDateOnly(date));
            }}
            dateFormat="yy-mm-dd"
            showIcon
            className={classNames('w-full', { 'p-invalid': errors?.deliveryDate })}
          />
          {errors?.deliveryDate && <small className="p-error">{errors.deliveryDate}</small>}
        </div>
      </div>

      <Divider align="left" className="my-2">
        <span className="text-sm font-semibold">
          Handling Units ({(item.handlingUnits || []).length})
        </span>
      </Divider>

      <HandlingUnitsTable
        item={item}
        itemIndex={index}
        newHU={newHU}
        onUpdateHandlingUnit={onUpdateHandlingUnit}
        onRemoveHandlingUnit={onRemoveHandlingUnit}
        onAddHandlingUnit={onAddHandlingUnit}
        onNewHUChange={onNewHUChange}
      />
    </div>
  );
};

export default ItemEditor;
